/**
 * Finds the topmost field element with an error in the form.
 * Note, that this works in conjunction with `RenderField` component, due to the
 * reliance on the `data-field-name` attribute.
 * @param {Object} formErrors - An object containing form error information, where keys are field names and values are error messages.
 * @param {Object} [formRef] - A reference to the form element. If no current formRef, defaults to `document`.
 * @returns {Object} An object containing the topmost field element and its top position relative to the document.
 * @returns {HTMLElement|null} [return.element] - The topmost field element with an error, or null if no error elements are found.
 * @returns {number|null} [return.top] - The top position of the topmost field element with an error, or null if no error elements are found.
 */
const getTopFieldElement = (formErrors, formRef) => {
  const formErrorKeys = Object.keys(formErrors);
  const { scrollY } = window;
  let topFieldObj = {
    element: null,
    top: null,
  };
  const container = formRef.current || document;
  formErrorKeys.forEach((fieldName) => {
    const fieldElement = container.querySelector(`[data-field-name="${fieldName}"]`);
    if (fieldElement) {
      const { top } = fieldElement.getBoundingClientRect();
      const fieldElementTop = top + scrollY;
      if (!topFieldObj.top || fieldElementTop < topFieldObj.top) {
        topFieldObj = {
          element: fieldElement,
          top: fieldElementTop,
        };
      }
    }
  });
  return topFieldObj;
};

/**
 * Time to wait after {@link focusValidationSummaryById} before scrolling and
 * focusing the first invalid field. NVDA needs enough time to finish the full
 * summary; shorter delays interrupt mid-sentence (~1s was too short in testing).
 */
export const VALIDATION_SUMMARY_TO_FIELD_DELAY_MS = 5000;

/**
 * Focuses the validation summary after paint so screen readers read it reliably.
 * `role="alert"` alone is often skipped when focus moves to another control immediately.
 * Call this in the `setState` callback after `formSubmissionError` is set, then wait
 * {@link VALIDATION_SUMMARY_TO_FIELD_DELAY_MS} before {@link scrollToFirstErrorField}.
 *
 * @param {string} summaryElementId - e.g. `register-validation-summary`
 */
export const focusValidationSummaryById = (summaryElementId) => {
  if (!summaryElementId) {
    return;
  }
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const el = document.getElementById(summaryElementId);
      if (el) {
        el.focus({ preventScroll: true });
      }
    });
  });
};

/**
 * First focusable control inside a field wrapper (label + input groups).
 * @param {HTMLElement} wrapper - Element with `data-field-name`.
 * @returns {HTMLElement|null}
 */
const getFocusableInWrapper = (wrapper) => {
  if (!wrapper) {
    return null;
  }
  const selector = [
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
  ].join(', ');
  return wrapper.querySelector(selector);
};

/**
 * Scrolls the first field with an error into view and moves keyboard/screen reader
 * focus to its control. Pair with {@link focusValidationSummaryById} and a delay of
 * {@link VALIDATION_SUMMARY_TO_FIELD_DELAY_MS} so the validation summary is announced first.
 * @param {Object} formErrors - Field name → error message
 * @param {Object} [formRef] - React ref to the form element
 */
export const scrollToFirstErrorField = (formErrors, formRef) => {
  const topFieldObj = getTopFieldElement(formErrors, formRef);
  if (!topFieldObj.element) {
    return;
  }

  const wrapper = topFieldObj.element;
  wrapper.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const el = getFocusableInWrapper(wrapper);
      if (el) {
        el.focus({ preventScroll: true });
      }
    });
  });
};
